/**
 * Avisos de WappTeam en toda la app (Esteban, 2026-10-09: reemplazo de Cliq con "on screen notifications").
 * Un solo lugar decide si un mensaje nuevo merece aviso: las ventanitas de abajo (wappteamDock) y la pantalla
 * completa de WappTeam (equipoView) dicen qué conversación tienen a la vista para no avisar de lo que ya se está leyendo.
 * Preferencias por computadora (localStorage): sonido y "no molestar".
 */
import { EquipoCanal } from "../../../services/v2/prospects.service";

/** Conversaciones que la persona tiene a la vista ahora mismo (pantalla WappTeam o ventanita abierta). */
const aLaVista = new Set<number>();
export const equipoVista = {
	poner(id: number | null, anterior?: number | null) {
		if (anterior) aLaVista.delete(anterior);
		if (id) aLaVista.add(id);
	},
	quitar(id: number | null) { if (id) aLaVista.delete(id); },
	/** Visible de verdad: la conversación está a la vista Y la pestaña del navegador está al frente. */
	viendo(id: number): boolean { return aLaVista.has(id) && document.visibilityState === "visible"; },
};

/**
 * Lo que un aviso "equipo" le hace a la lista de conversaciones, sin pedirla al servidor (26-oct-2026: la lista entera,
 * /_equipo, era lo que más trabajaba el servidor; cada mensaje, reacción o edición la hacía repedir en cientos de pestañas).
 * - Mensaje nuevo: la fila sube con lo que trae el aviso (de, texto, menciones).
 * - Cambio (edición, borrado, reacción, fijado): el servidor dice si toca la lista ("lista": 0/1) y, si es el último
 *   mensaje, su texto nuevo ("ultimo", mismo formato que la lista).
 * Solo se pide la lista cuando el aviso no alcanza: conversación que no tengo, renombrar grupo, o un servidor viejo.
 */
export const avisoPideLista = (d: any, canales: EquipoCanal[]): boolean => {
	const cid = Number(d?.c);
	if (!cid || !canales.some((x) => x.id === cid)) return true;
	return d?.cambio ? d?.lista == null : !d?.de;
};
/** La lista con el aviso aplicado. Llamar solo si avisoPideLista dio false. */
export const listaConAviso = (cs: EquipoCanal[], d: any, yoId: number | null): EquipoCanal[] => {
	const cid = Number(d?.c);
	const i = cs.findIndex((x) => x.id === cid);
	if (i < 0) return cs;
	if (d?.cambio) {
		if (Number(d.lista) !== 1) return cs;
		return cs.map((x) => (x.id === cid ? { ...x, ultimo: d.ultimo ?? null } : x));
	}
	// Respuesta en hilo que no se mandó también a la conversación: la fila no se mueve, como Slack.
	if (Number(d?.h) && !d?.ht) return cs;
	const mio = Number(d.u) === yoId;
	const viendo = equipoVista.viendo(cid);
	const mencion = Array.isArray(d.menciones) && !!yoId && d.menciones.includes(yoId);
	const c = cs[i];
	const cuenta = !mio && !viendo && (!c.silenciado || mencion || c.menciones > 0);
	const n: EquipoCanal = { ...c, ultimo: `${d.de}||${String(d.texto || "")}`, last_message_at: new Date().toISOString(),
		no_leidos: cuenta ? (c.no_leidos || 0) + 1 : c.no_leidos, pendientes: !mio && !viendo ? (c.pendientes || 0) + 1 : c.pendientes, menciones: mencion && !mio && !viendo ? (c.menciones || 0) + 1 : c.menciones };
	const resto = cs.filter((_, j) => j !== i);
	// Mismo orden que el servidor: IA fija, las que fijé yo, anuncios fijados, y luego lo más reciente arriba.
	const grupo = (x: EquipoCanal) => (x.fijo ? 0 : x.fijada ? 1 : x.anuncioFijo ? 2 : 3);
	const k = resto.filter((x) => grupo(x) < grupo(n)).length;
	return [...resto.slice(0, k), n, ...resto.slice(k)];
};

const leer = (k: string, def: string): string => { try { return localStorage.getItem(k) ?? def; } catch { return def; } };
const guardar = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* sin almacenamiento */ } };

export const prefs = {
	get sonido(): boolean { return leer("wteam.sonido", "1") === "1"; },
	set sonido(v: boolean) { guardar("wteam.sonido", v ? "1" : "0"); },
	get noMolestar(): boolean { return leer("wteam.noMolestar", "0") === "1"; },
	set noMolestar(v: boolean) { guardar("wteam.noMolestar", v ? "1" : "0"); },
	/** Ya se le ofreció activar los avisos del navegador y dijo "Ahora no". */
	get avisoDescartado(): boolean { return leer("wteam.avisoDescartado", "0") === "1"; },
	set avisoDescartado(v: boolean) { guardar("wteam.avisoDescartado", v ? "1" : "0"); },
};

export const permisoAvisos = (): NotificationPermission | "no-soportado" =>
	(typeof Notification === "undefined" ? "no-soportado" : Notification.permission);

export const pedirPermisoAvisos = async (): Promise<NotificationPermission | "no-soportado"> => {
	if (typeof Notification === "undefined") return "no-soportado";
	try { return await Notification.requestPermission(); } catch { return Notification.permission; }
};

/** Aviso del sistema (fuera del navegador). Al tocarlo, vuelve a la pestaña y abre la conversación. */
export const avisoDelSistema = (titulo: string, cuerpo: string, tag: string, alTocar: () => void) => {
	if (permisoAvisos() !== "granted") return;
	try {
		const n = new Notification(titulo, { body: cuerpo, tag, icon: "/favicon.ico" });
		n.onclick = () => { try { window.focus(); } catch { /* */ } alTocar(); n.close(); };
		setTimeout(() => n.close(), 12000);
	} catch { /* el navegador no deja (iPhone fuera de la app instalada) */ }
};

/** "Tin" corto hecho con el navegador (sin archivo de audio). Los navegadores lo callan hasta el primer clic en la página. */
let ctx: AudioContext | null = null;
let ultimoSonido = 0;
export const sonar = () => {
	if (!prefs.sonido || Date.now() - ultimoSonido < 1500) return;
	ultimoSonido = Date.now();
	try {
		const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
		if (!AC) return;
		ctx = ctx || new AC();
		const c = ctx as AudioContext;
		if (c.state === "suspended") c.resume().catch(() => null);
		const t = c.currentTime;
		[880, 1320].forEach((f, i) => {
			const o = c.createOscillator();
			const g = c.createGain();
			o.type = "sine";
			o.frequency.value = f;
			g.gain.setValueAtTime(0.0001, t + i * 0.09);
			g.gain.exponentialRampToValueAtTime(0.12, t + i * 0.09 + 0.02);
			g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.09 + 0.18);
			o.connect(g).connect(c.destination);
			o.start(t + i * 0.09);
			o.stop(t + i * 0.09 + 0.2);
		});
	} catch { /* sin audio */ }
};
